"use strict";
/**
 * RxNorm API Service
 *
 * Uses the NIH National Library of Medicine's RxNorm API for standardized
 * drug/allergy name lookup. The cross-reactivity data remains curated
 * since RxNorm drug classes don't represent true immunological cross-reactivity.
 *
 * API Documentation: https://lhncbc.nlm.nih.gov/RxNav/APIs/RxNormAPIs.html
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.searchAllergies = searchAllergies;
exports.getDrugInfo = getDrugInfo;
exports.getSpellingSuggestions = getSpellingSuggestions;
exports.searchDrugs = searchDrugs;
exports.searchAllAllergies = searchAllAllergies;
var RXNORM_BASE_URL = 'https://rxnav.nlm.nih.gov/REST';
/**
 * Search for drugs/substances by approximate string match
 * Uses the /approximateTerm.json endpoint for fuzzy matching
 */
function searchAllergies(query) {
    return __awaiter(this, void 0, Promise, function () {
        var url, response, data, suggestions, error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!query || query.length < 2)
                        return [2 /*return*/, []];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, , 5]);
                    url = "".concat(RXNORM_BASE_URL, "/approximateTerm.json?term=").concat(encodeURIComponent(query), "&maxEntries=15");
                    return [4 /*yield*/, fetch(url)];
                case 2:
                    response = _b.sent();
                    if (!response.ok) {
                        console.warn('[RxNorm] API request failed:', response.status);
                        return [2 /*return*/, fallbackSearch(query)];
                    }
                    return [4 /*yield*/, response.json()];
                case 3:
                    data = _b.sent();
                    if (!((_a = data.approximateGroup) === null || _a === void 0 ? void 0 : _a.candidate)) {
                        return [2 /*return*/, fallbackSearch(query)];
                    }
                    suggestions = data.approximateGroup.candidate
                        .filter(function (c) { return c.rxcui && c.name; })
                        .map(function (c) { return ({
                        rxcui: c.rxcui,
                        name: c.name,
                        tty: c.tty || ''
                    }); })
                        // Remove duplicates by name
                        .filter(function (item, index, self) {
                        return index === self.findIndex(function (t) { return t.name.toLowerCase() === item.name.toLowerCase(); });
                    })
                        .slice(0, 10);
                    // If no results, fall back to local list
                    if (suggestions.length === 0) {
                        return [2 /*return*/, fallbackSearch(query)];
                    }
                    return [2 /*return*/, suggestions];
                case 4:
                    error_1 = _b.sent();
                    console.warn('[RxNorm] API error, using fallback:', error_1);
                    return [2 /*return*/, fallbackSearch(query)];
                case 5: return [2 /*return*/];
            }
        });
    });
}
/**
 * Get drug/substance details by RxCUI
 */
function getDrugInfo(rxcui) {
    return __awaiter(this, void 0, Promise, function () {
        var url, response, data, props, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    url = "".concat(RXNORM_BASE_URL, "/rxcui/").concat(rxcui, "/properties.json");
                    return [4 /*yield*/, fetch(url)];
                case 1:
                    response = _a.sent();
                    if (!response.ok)
                        return [2 /*return*/, null];
                    return [4 /*yield*/, response.json()];
                case 2:
                    data = _a.sent();
                    props = data.properties;
                    if (!props)
                        return [2 /*return*/, null];
                    return [2 /*return*/, {
                            rxcui: props.rxcui,
                            name: props.name,
                            tty: props.tty
                        }];
                case 3:
                    error_2 = _a.sent();
                    console.warn('[RxNorm] Failed to get drug info:', error_2);
                    return [2 /*return*/, null];
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Get spelling suggestions for a term
 * Useful for autocorrection
 */
function getSpellingSuggestions(term) {
    return __awaiter(this, void 0, Promise, function () {
        var url, response, data, error_3;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 3, , 4]);
                    url = "".concat(RXNORM_BASE_URL, "/spellingsuggestions.json?name=").concat(encodeURIComponent(term));
                    return [4 /*yield*/, fetch(url)];
                case 1:
                    response = _c.sent();
                    if (!response.ok)
                        return [2 /*return*/, []];
                    return [4 /*yield*/, response.json()];
                case 2:
                    data = _c.sent();
                    return [2 /*return*/, ((_b = (_a = data.suggestionGroup) === null || _a === void 0 ? void 0 : _a.suggestionList) === null || _b === void 0 ? void 0 : _b.suggestion) || []];
                case 3:
                    error_3 = _c.sent();
                    return [2 /*return*/, []];
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Search using the /drugs endpoint for more specific drug matches
 */
function searchDrugs(name) {
    return __awaiter(this, void 0, Promise, function () {
        var url, response, data, drugGroup, suggestions, _i, _a, group, _b, _c, prop, error_4;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    if (!name || name.length < 2)
                        return [2 /*return*/, []];
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 4, , 5]);
                    url = "".concat(RXNORM_BASE_URL, "/drugs.json?name=").concat(encodeURIComponent(name));
                    return [4 /*yield*/, fetch(url)];
                case 2:
                    response = _d.sent();
                    if (!response.ok)
                        return [2 /*return*/, []];
                    return [4 /*yield*/, response.json()];
                case 3:
                    data = _d.sent();
                    drugGroup = data.drugGroup;
                    if (!(drugGroup === null || drugGroup === void 0 ? void 0 : drugGroup.conceptGroup))
                        return [2 /*return*/, []];
                    suggestions = [];
                    for (_i = 0, _a = drugGroup.conceptGroup; _i < _a.length; _i++) {
                        group = _a[_i];
                        if (group.conceptProperties) {
                            for (_b = 0, _c = group.conceptProperties; _b < _c.length; _b++) {
                                prop = _c[_b];
                                suggestions.push({
                                    rxcui: prop.rxcui,
                                    name: prop.name,
                                    synonym: prop.synonym,
                                    tty: prop.tty
                                });
                            }
                        }
                    }
                    return [2 /*return*/, suggestions.slice(0, 10)];
                case 4:
                    error_4 = _d.sent();
                    return [2 /*return*/, []];
                case 5: return [2 /*return*/];
            }
        });
    });
}
/**
 * Fallback to local common allergies list when API is unavailable
 * Includes both drug and non-drug allergens (food, environmental)
 */
var LOCAL_ALLERGIES = [
    // Common Drug Allergies
    'Penicillin', 'Amoxicillin', 'Ampicillin', 'Cephalexin', 'Cephalosporins',
    'Sulfonamides', 'Sulfamethoxazole', 'Trimethoprim-Sulfamethoxazole',
    'Aspirin', 'Ibuprofen', 'Naproxen', 'Celecoxib', 'NSAIDs',
    'Codeine', 'Morphine', 'Hydrocodone', 'Oxycodone', 'Tramadol', 'Fentanyl',
    'ACE Inhibitors', 'Lisinopril', 'Enalapril', 'Captopril',
    'Beta Blockers', 'Metoprolol', 'Atenolol', 'Propranolol',
    'Tetracycline', 'Doxycycline', 'Minocycline',
    'Erythromycin', 'Azithromycin', 'Clarithromycin',
    'Ciprofloxacin', 'Levofloxacin', 'Fluoroquinolones',
    'Metronidazole', 'Vancomycin', 'Clindamycin',
    'Insulin', 'Metformin', 'Glipizide',
    'Statins', 'Atorvastatin', 'Simvastatin', 'Rosuvastatin',
    'Contrast Dye', 'Iodinated Contrast Media', 'Gadolinium',
    'Lidocaine', 'Novocaine', 'Bupivacaine',
    'Heparin', 'Warfarin', 'Enoxaparin',
    'Phenytoin', 'Carbamazepine', 'Lamotrigine', 'Valproic Acid',
    'Allopurinol', 'Colchicine',
    'Gabapentin', 'Pregabalin',
    'Omeprazole', 'Pantoprazole', 'Esomeprazole',
    // Food Allergies
    'Peanuts', 'Tree Nuts', 'Almonds', 'Walnuts', 'Cashews', 'Pistachios', 'Hazelnuts', 'Pecans', 'Macadamia Nuts',
    'Shellfish', 'Shrimp', 'Crab', 'Lobster', 'Crayfish', 'Oysters', 'Clams', 'Mussels', 'Scallops',
    'Fish', 'Salmon', 'Tuna', 'Cod', 'Tilapia',
    'Eggs', 'Egg Whites', 'Egg Yolks',
    'Milk', 'Dairy', 'Lactose', 'Casein', 'Whey',
    'Soy', 'Soybeans',
    'Wheat', 'Gluten',
    'Sesame', 'Sesame Seeds',
    'Mustard',
    'Corn',
    // Environmental / Other
    'Latex', 'Natural Rubber Latex',
    'Bee Venom', 'Bee Stings', 'Wasp Stings', 'Insect Stings', 'Fire Ant Stings',
    'Dust Mites', 'House Dust',
    'Pet Dander', 'Cat Dander', 'Dog Dander',
    'Pollen', 'Grass Pollen', 'Tree Pollen', 'Ragweed',
    'Mold', 'Mold Spores',
    'Nickel', 'Chromium', 'Cobalt',
    'Formaldehyde',
    'Adhesive Tape',
    'Chlorhexidine'
];
function fallbackSearch(query) {
    var lowerQuery = query.toLowerCase();
    return LOCAL_ALLERGIES
        .filter(function (a) { return a.toLowerCase().includes(lowerQuery); })
        .map(function (name) { return ({
        rxcui: '', // No RxCUI for local items
        name: name,
        tty: 'LOCAL'
    }); })
        .slice(0, 10);
}
/**
 * Combined search: tries RxNorm first, supplements with local non-drug allergens
 * This ensures food and environmental allergies are always available
 */
function searchAllAllergies(query) {
    return __awaiter(this, void 0, Promise, function () {
        var _a, rxnormResults, localResults, seen, localUnique;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!query || query.length < 2)
                        return [2 /*return*/, []];
                    return [4 /*yield*/, Promise.all([
                            searchAllergies(query),
                            Promise.resolve(fallbackSearch(query))
                        ])
                        // Merge results, prioritizing RxNorm but including local non-duplicates
                    ];
                case 1:
                    _a = _b.sent(), rxnormResults = _a[0], localResults = _a[1];
                    seen = new Set(rxnormResults.map(function (r) { return r.name.toLowerCase(); }));
                    localUnique = localResults.filter(function (r) { return !seen.has(r.name.toLowerCase()); });
                    return [2 /*return*/, __spreadArray(__spreadArray([], rxnormResults, true), localUnique, true).slice(0, 12)];
            }
        });
    });
}
